import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { catalog, type ApiProduct } from "@/lib/api";
import { savings } from "@/lib/pdp-pricing";
import { firstAdditionalStill, primaryImage } from "@/lib/product-images";
import { ProductCard } from "./ProductCard";
import { QuickAddSheet } from "./QuickAddSheet";
export function useProducts() {
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    catalog
      .list()
      .then((r) => {
        if (active) setProducts(r.products ?? []);
      })
      .catch(() => {
        if (active) setError("Katalog gagal dimuat. Silakan muat ulang halaman.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  return { products, loading, error };
}
export function CatalogProducts({ line, slugs }: { line?: string; slugs?: string[] }) {
  const { products, loading, error } = useProducts();
  // Picked products, in the order picked (a campaign page); else a line.
  const shown = slugs?.length
    ? slugs.flatMap((s) => products.filter((p) => p.slug === s))
    : products.filter((p) => !line || p.line === line);
  if (loading) return <CatalogSkeleton />;
  if (error)
    return (
      <p className="mt-8" role="alert">
        {error}
      </p>
    );
  if (!shown.length) return <p className="mt-8">Belum ada produk tersedia.</p>;
  return (
    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {shown.map((p) => (
        <LiveProductCard key={p.id} product={p} />
      ))}
    </div>
  );
}
/** Placeholder cards in the grid's own shape, so nothing jumps when it loads. */
export function CatalogSkeleton({
  count = 6,
  className = "mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3",
}: { count?: number; className?: string } = {}) {
  return (
    <div className={className} role="status" aria-label="Memuat katalog">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex flex-col overflow-hidden rounded-2xl border border-ink/8 bg-white">
          <Skeleton className="aspect-[4/3] rounded-none" />
          <div className="flex flex-col gap-2 p-5">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="mt-4 h-5 w-1/3" />
          </div>
          <div className="px-5 pb-5">
            <Skeleton className="h-9 w-full rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
export function LiveProductCard({ product: p }: { product: ApiProduct }) {
  const cheapest = [...(p.skus ?? [])].sort((a, b) => a.priceIdr - b.priceIdr)[0];
  const first = cheapest?.packageItems?.[0]?.sku?.product;
  // primaryImage, not images[0]: an item whose only uploads are Additional
  // photos may lead with a video, which no card can show. A package's own
  // Additional still still beats borrowing an item's photo.
  const image =
    primaryImage(p)?.publicUrl ??
    firstAdditionalStill(p)?.publicUrl ??
    (first ? primaryImage(first)?.publicUrl : undefined);
  // savings, not the bare compare-at: a package the server flagged invalid has
  // one and no honest saving to claim.
  const save = cheapest ? savings(cheapest) : null;
  const paketHemat = Boolean(cheapest?.isPackage && save);
  const [quickAdd, setQuickAdd] = useState(false);
  return (
    <>
      <ProductCard
        paketHemat={paketHemat}
        slug={p.slug}
        name={p.name}
        tagline={p.tagline}
        image={image}
        fromPriceIdr={cheapest?.priceIdr ?? 0}
        soldOut={p.soldOut}
        compareAtIdr={save ? cheapest?.compareAtIdr : undefined}
        onQuickAdd={() => setQuickAdd(true)}
      />
      <QuickAddSheet slug={p.slug} name={p.name} open={quickAdd} onOpenChange={setQuickAdd} />
    </>
  );
}
